import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  FunctionCoach,
  SearchCoachPortalParticipantsParams,
} from "../../../api/services/program/models";
import { useSearchCoachParticipantMutation } from "../../../api/services/program/programApis";
import { useGetUsersDetailByRoleMutation } from "../../../api/services/user/userApi";
import { InformationModal, OptionsDataType } from "../../../components";
import { BE_ERROR_CODES, ROLE_IDS } from "../../../helpers/constants";
import {
  getFieldUndefinedForPayloadParams,
  getParsedBEErrors,
  getValueOrBlank,
  getValueOrBlankArray,
  isAllParticipants,
  isBETimedOutError,
  isMyCaseLoads,
  isNotEmpty,
} from "../../../helpers/utils";
import { useEnterKeyAction } from "../../../hooks/useEnterKeyAction";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { useToast } from "../../../hooks/useToast";
import { toggleLoader } from "../../../redux/slices/appState/appStateSlice";
import { convertFunctionsOrCoachToDropdown } from "../../createProgram/helper";
import {
  AGEING_FILTER_KEYS,
  ParticipantSearchResultRowValue,
  ParticipantsSearchFormKey,
  SEARCH_TYPE_KEY,
} from "../modelsAndSchemas/Models";
import { SearchParticipantSchema } from "../modelsAndSchemas/ValidationSchema";
import { searchParticipantsSearchSectionSiteCoreKeys } from "../sitecore/SiteCoreKeys";
import { ParticipantsMainWrapper } from "./ParticipantsMain.style";
import { ParticipantsSearchHeader } from "./components/participantsSearchHeader/ParticipantsSearchHeader";
import { ParticipantsSearchResult } from "./components/participantsSearchResults/ParticipantsSearchResult";
import { ParticipantsSearch } from "./participantsSearch/ParticipantsSearch";

export const participantsSearchDefaultValues = {
  [ParticipantsSearchFormKey.resetForm]: false,
  [ParticipantsSearchFormKey.searchType]: SEARCH_TYPE_KEY.myCaseLoads,
  [ParticipantsSearchFormKey.participantFirstName]: "",
  [ParticipantsSearchFormKey.participantFirstNameFilter]:
    searchParticipantsSearchSectionSiteCoreKeys.contains,
  [ParticipantsSearchFormKey.participantLastName]: "",
  [ParticipantsSearchFormKey.participantLastNameFilter]:
    searchParticipantsSearchSectionSiteCoreKeys.contains,
  [ParticipantsSearchFormKey.keyNumber]: "",
  [ParticipantsSearchFormKey.keyNumberFilter]:
    searchParticipantsSearchSectionSiteCoreKeys.contains,
  [ParticipantsSearchFormKey.participantEmail]: "",
  [ParticipantsSearchFormKey.participantEmailFilter]:
    searchParticipantsSearchSectionSiteCoreKeys.contains,
  [ParticipantsSearchFormKey.mobileISOCode]: "",
  [ParticipantsSearchFormKey.mobileCountryCode]: "",
  [ParticipantsSearchFormKey.participantMobileNumber]: "",
  [ParticipantsSearchFormKey.clientName]: "",
  [ParticipantsSearchFormKey.clientNameFilter]:
    searchParticipantsSearchSectionSiteCoreKeys.contains,
  [ParticipantsSearchFormKey.ageing]: AGEING_FILTER_KEYS.ALL,
  [ParticipantsSearchFormKey.coachIds]: [],
  [ParticipantsSearchFormKey.coachType]: "",
  [ParticipantsSearchFormKey.participantStatus]: [],
};

export const ParticipantsMain: FC = () => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { commonLabels, commonValidationMessages, useFetchLabel } =
    useSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [isSearchClick, setIsSearchClick] = useState(false);
  const [participantsResultData, setParticipantsResultData] = useState<
    ParticipantSearchResultRowValue[]
  >([]);
  const [allCoachesList, setAllCoachesList] = useState<OptionsDataType[]>([]);
  const [makeMobileFieldFocussed, setMakeMobileFieldFocussed] = useState(false);
  const methods = useForm({
    mode: "all",
    defaultValues: participantsSearchDefaultValues,
    resolver: yupResolver(SearchParticipantSchema(commonValidationMessages)),
  });
  const { getValues, setValue, handleSubmit, control } = methods;
  const location = useLocation();
  const from = location?.state?.from;
  const getLabel = useFetchLabel(
    searchParticipantsSearchSectionSiteCoreKeys.searchParticipantsSearchSectionTitle,
  );

  useEffect(() => {
    if (from && isNotEmpty(from)) {
      setValue(ParticipantsSearchFormKey.searchType, from);
    }
  }, [from]);

  const {
    mutateAsync: getCoachPortalParticipants,
    isPending,
    data: participantsResult,
    isError: isSearchParticipantError,
    error: searchParticipantError,
    isSuccess: isSearchParticipantSuccess,
  } = useSearchCoachParticipantMutation();

  const {
    mutateAsync: getCoachList,
    data: coachesList,
    isPending: isCoachesListPending,
    isError: isCoachesListError,
    isSuccess: isCoachesListSuccess,
  } = useGetUsersDetailByRoleMutation();

  useEffect(() => {
    getCoachList({ roleIds: [ROLE_IDS.deliveryCoach] });
  }, []);

  useEffect(() => {
    if (isCoachesListSuccess && coachesList) {
      setAllCoachesList(
        convertFunctionsOrCoachToDropdown(coachesList as FunctionCoach[]),
      );
    }
  }, [isCoachesListSuccess, coachesList]);

  useEffect(() => {
    if (isSearchParticipantSuccess && participantsResult) {
      setParticipantsResultData(
        participantsResult?.data as ParticipantSearchResultRowValue[],
      );
    }
  }, [isSearchParticipantSuccess, participantsResult]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isSearchParticipantsPending: {
          isVisible: isPending || isCoachesListPending,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isSearchParticipantsPending: {
            isVisible: false,
            message: "",
          },
        }),
      );
    };
  }, [isPending, isCoachesListPending]);

  useEffect(() => {
    if (isCoachesListError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isCoachesListError]);

  useEffect(() => {
    if (isSearchParticipantError) {
      dispatch(toggleLoader({ isVisible: false }));
      setParticipantsResultData([]);
      const error = getParsedBEErrors(searchParticipantError);
      if (
        (isNotEmpty(error) &&
          error[0]?.errorCode ===
            BE_ERROR_CODES.COACH_PORTAL_SEARCH_PARTICIPANTS_LIMIT_EXCEEDED) ||
        isBETimedOutError(error)
      ) {
        setShowErrorMessage(true);
        setIsSearchClick(false);
      }
    }
  }, [searchParticipantError, isSearchParticipantError]);

  const handleSearchSubmit = () => {
    setIsSearchClick(true);
    callSearchApi();
  };

  const callSearchApi = () => {
    const {
      participantFirstName,
      participantFirstNameFilter,
      participantLastName,
      participantLastNameFilter,
      keyNumber,
      keyNumberFilter,
      participantEmail,
      participantEmailFilter,
      mobileISOCode,
      mobileCountryCode,
      participantMobileNumber,
      clientName,
      clientNameFilter,
      ageing,
      participantStatus,
      coachIds,
      searchType,
      coachType,
    } = getValues();

    let params: SearchCoachPortalParticipantsParams = {
      coachIds: isMyCaseLoads(searchType)
        ? [getValueOrBlank(loggedInUser?.id)]
        : (coachIds ?? []),
      ...getFieldUndefinedForPayloadParams({
        coachTypeId: coachType,
        participantFirstName,
        participantFirstNameFilter,
        participantLastName,
        participantLastNameFilter,
        keyNumber,
        keyNumberFilter,
        participantEmail,
        participantEmailFilter,
        mobileISOCode: participantMobileNumber ? mobileISOCode : "",
        mobileCountryCode: participantMobileNumber ? mobileCountryCode : "",
        participantMobileNumber,
        clientName,
        clientNameFilter,
        aging: isMyCaseLoads(searchType) ? ageing : undefined,
        programStatuses: isAllParticipants(searchType)
          ? getValueOrBlankArray(participantStatus)
          : undefined,
      }),
    };
    getCoachPortalParticipants(params);
  };

  const handleCloseErrorMessage = () => {
    setMakeMobileFieldFocussed(!makeMobileFieldFocussed);
    setShowErrorMessage(false);
  };

  useEnterKeyAction({
    onEnter: handleSubmit(handleSearchSubmit),
  });

  const resetFormValue = useWatch({
    name: ParticipantsSearchFormKey.resetForm,
    control,
  });

  useEffect(() => {
    setParticipantsResultData([]);
  }, [resetFormValue]);

  return (
    <FormProvider {...methods}>
      <ParticipantsMainWrapper data-testid="participants-search">
        <ParticipantsSearchHeader />
        <form
          onSubmit={handleSubmit(handleSearchSubmit)}
          data-testid="participants-search-form"
        >
          <ParticipantsSearch
            coachesList={allCoachesList}
            setIsSearchClick={setIsSearchClick}
            makeMobileFieldFocussed={makeMobileFieldFocussed}
          />
        </form>

        <ParticipantsSearchResult
          rows={participantsResultData}
          isSearch={isSearchClick}
        />
        {showErrorMessage && (
          <InformationModal
            showModal={showErrorMessage}
            handleModalClose={handleCloseErrorMessage}
            handlePrimaryButtonClick={handleCloseErrorMessage}
            message={commonLabels.fineTuningSearchCriteriaMessage}
            data-testid="participants-info-modal"
          />
        )}
      </ParticipantsMainWrapper>
    </FormProvider>
  );
};
